import { Stack, TextField, IconButton, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';

import { colors, theme } from '../theme/theme';

const MAX_QUERY_CHARS = 200;

interface ChatInputProps {
    onSendMessage: (message: string) => void;
    isLoading: boolean;
    brandPrimaryColor: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading, brandPrimaryColor }) => {
    const [message, setMessage] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const trimmedMessage = message.trim();
    const charCount = message.length;
    const isOverCharLimit = charCount > MAX_QUERY_CHARS;
    const isInvalid = isOverCharLimit;
    const isNearCharLimit = charCount >= 160;

    const handleSend = () => {
        if (!trimmedMessage) return;
        if (isOverCharLimit) {
            setValidationError(`Please keep your query within ${MAX_QUERY_CHARS} characters.`);
            return;
        }

        setValidationError(null);
        onSendMessage(trimmedMessage);
        setMessage('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            gap={1}
            sx={{
                p: 2,
                backgroundColor: colors.fullWhite,
                borderTop: `1px solid ${colors.greyPreset[30]}`,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <TextField
                    id="chatbot-message-input"
                    fullWidth
                    placeholder="Type your message..."
                    value={message}
                    slotProps={{ htmlInput: { 'aria-label': 'Type your message' } }}
                    onChange={e => {
                        const nextValue = e.target.value;
                        if (nextValue.length <= MAX_QUERY_CHARS) {
                            setMessage(nextValue);
                        } else {
                            setMessage(nextValue.slice(0, MAX_QUERY_CHARS));
                        }

                        if (validationError) {
                            setValidationError(null);
                        }
                    }}
                    onKeyDown={handleKeyPress}
                    disabled={isLoading}
                    size="small"
                    variant="outlined"
                    multiline
                    maxRows={3}
                    helperText={
                        validationError || (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={1}
                                flexWrap="nowrap"
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: colors.fullBlack,
                                        flex: 1,
                                        minWidth: 0,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    Keep your query short and specific for best results.
                                </Typography>
                                <Stack direction="row" spacing={0.6} flexWrap="nowrap" sx={{ flexShrink: 0 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            px: 0.9,
                                            py: 0.2,
                                            borderRadius: '999px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: alpha(brandPrimaryColor, 0.95),
                                            backgroundColor: isNearCharLimit
                                                ? alpha(brandPrimaryColor, 0.24)
                                                : alpha(brandPrimaryColor, 0.12),
                                            border: `1px solid ${alpha(brandPrimaryColor, isNearCharLimit ? 0.35 : 0.22)}`,
                                        }}
                                    >
                                        {charCount}/{MAX_QUERY_CHARS} chars
                                    </Box>
                                </Stack>
                            </Stack>
                        )
                    }
                    sx={{
                        '& textarea[aria-hidden="true"]': {
                            visibility: 'hidden',
                        },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: colors.fullWhite,
                            fontSize: 14,
                            [theme.breakpoints.down('md')]: {
                                fontSize: 16,
                            },
                            '& fieldset': {
                                borderColor: alpha(brandPrimaryColor, 0.3),
                            },
                            '&:hover fieldset': {
                                borderColor: alpha(brandPrimaryColor, 0.55),
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: brandPrimaryColor,
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            '&::placeholder': {
                                opacity: 0.7,
                            },
                        },
                        '& .MuiFormHelperText-root': {
                            mx: 0.2,
                            mt: 0.6,
                            fontSize: 12,
                            lineHeight: 1.35,
                            color: validationError ? alpha(brandPrimaryColor, 0.95) : alpha(brandPrimaryColor, 0.9),
                        },
                    }}
                />
            </div>
            <IconButton
                onClick={handleSend}
                disabled={isLoading || !trimmedMessage || isInvalid}
                sx={{
                    backgroundColor: brandPrimaryColor,
                    color: colors.fullWhite,
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    alignSelf: 'flex-start',
                    mt: '2px',
                    '&:hover': {
                        backgroundColor: brandPrimaryColor,
                        filter: 'brightness(0.92)',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: alpha(brandPrimaryColor, 0.28),
                        color: alpha(theme.palette.common.white, 0.9),
                    },
                }}
            >
                <span className="icon-send" style={{ fontSize: 18 }} />
            </IconButton>
        </Stack>
    );
};
